import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  TextField,
  Typography,
  Alert,
  Container,
  IconButton,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import { PrivacyPolicyDialog } from './PrivacyPolicy';
import { TermsOfServiceDialog } from './TermsOfService';
import { UbAccountService } from '../../api/UbAccountService';
import { UB_Account } from '../../api/protosCompiled/ub_account/ub_account_pb';
import {
  AccountFormErrors,
  initialErrors,
  validateEmailFormat,
  isStrongPassword,
} from './helperFile';
import { VisibilityOff, Visibility } from '@mui/icons-material';
import { useStore } from 'zustand';
import { useLocalStorage } from '../../stores/localStorage';
import { sendVerificationEmail } from './checkoutHelper';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';

type CreateAccountProps = {
  accountId: string;
  setAccountId: (accountId: string) => void;
  onNextStep: () => void;
};

export const CreateAccount: React.FC<CreateAccountProps> = ({
  accountId,
  setAccountId,
  onNextStep,
}) => {
  const theme = useTheme();
  const { darkMode } = useCustomTheme();

  //use the unauth key to allow the verification email service to recognize the front end
  const unAuthKey = process.env.REACT_APP_UNAUTH_KEY || '';

  // verification code that will be sent to the user's email
  const [verificationCode, setVerificationCode] = useState('');
  // user entered verification code
  const [userEnteredCode, setUserEnteredCode] = useState('');
  // dialog open state
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  // verification error
  const [verificationError, setVerificationError] = useState<string | null>(null);
  // loading state for sending verification email
  const [sendingVerification, setSendingVerification] = useState(false);

  const setSessionToken = useStore(useLocalStorage).setSessionToken;
  const [accountName, setAccountName] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [accountPhone, setAccountPhone] = useState('');
  // const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [termsOfService, setTermsOfService] = useState(false);
  const [errors, setErrors] = useState<AccountFormErrors>(initialErrors);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const [captchaQuestion] = useState(() => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    return { a, b, answer: String(a + b) };
  });

  const handleAccountNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccountName(e.target.value);
    setErrors(prev => ({ ...prev, accountName: '' }));
  };

  const handleAccountEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccountEmail(e.target.value);
    setErrors(prev => ({ ...prev, email: '' }));
  };

  const handleAccountPhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccountPhone(e.target.value);
    setErrors(prev => ({ ...prev, phoneNumber: '' }));
  };

  const handleUserNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserName(e.target.value);
    setErrors(prev => ({ ...prev, username: '' }));
  };

  const handleTermsOfServiceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTermsOfService(e.target.checked);
    setErrors(prev => ({ ...prev, terms: '' }));
  };

  // Generate a random 6-digit verification code
  const generateVerificationCode = () => {
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setVerificationCode(code);
    return code;
  };

  // Send verification email
  const sendVerificationEmailToUser = async () => {
    setSendingVerification(true);
    setVerificationError(null);

    try {
      const code = generateVerificationCode();
      // Use the email that will be the user's email (typically the same as account email)
      const nameToUse = userName || accountName;
      const success = await sendVerificationEmail(accountEmail, nameToUse, code, unAuthKey);

      if (!success) {
        throw new Error('Failed to send verification email');
      }

      // Open verification dialog
      setVerificationDialogOpen(true);
    } catch (error) {
      console.error('Verification email error:', error);
      setVerificationError('Failed to send verification email. Please try again.');
    } finally {
      setSendingVerification(false);
    }
  };

  // Verify code entered by user
  const verifyCode = () => {
    if (userEnteredCode === verificationCode) {
      // Close dialog and proceed with account creation
      setVerificationDialogOpen(false);
      createAccount();
    } else {
      setVerificationError('Incorrect verification code. Please try again.');
    }
  };

  const validateForm = () => {
    const newErrors = { ...initialErrors };
    let isValid = true;

    if (!accountName.trim()) {
      newErrors.accountName = 'Account name is required';
      isValid = false;
    }

    if (!validateEmailFormat(accountEmail)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    if (accountPhone.length < 10) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
      isValid = false;
    }

    if (!isStrongPassword(userPassword)) {
      newErrors.password =
        'Password must be at least 8 characters and include a number, uppercase and lowercase letter';
      isValid = false;
    }

    if (userPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    if (captchaAnswer !== captchaQuestion.answer) {
      newErrors.captcha = 'Captcha answer is incorrect';
      isValid = false;
    }

    if (!termsOfService) {
      newErrors.terms = 'You must agree to the terms and conditions';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Start the verification process
  const handleInitiateVerification = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      sendVerificationEmailToUser();
    }
  };

  // Create account AFTER verification
  const createAccount = async () => {
    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      const accountData: UB_Account.AsObject = {
        accountid: '', //set in the backend
        accountname: accountName,
        accountemail: accountEmail,
        accountphone: accountPhone,
        gcsbucketname: '', //set in the backend
        numusers: 1, //updated via webhook upon successful checkout
        accountisactive: true, //set in the backend
        user: {
          userid: '', //set in the backend
          username: userName,
          useremail: accountEmail, //we will set the account and user email to the same value
          userpassword: userPassword,
          userisactive: true,
          userphotourl: '',
          roleid: 'a63e5c03-71a6-47ee-b490-480cc7aa3b4f', //the primary account must also be level 1
          rolename: 'Level 1', //the primary account must also be level 1
          isprimaryadmin: true, // the primary account must also be the primary admin
          isloggedin: false,
        },
      };

      const response = await UbAccountService.createAccount(accountData);
      if (response.id) {
        setAccountId(response.id);
        //set the session token in local storage
        setSessionToken(response.sessiontoken);
        onNextStep();
      } else {
        setSubmissionError(
          'Failed to create account. If the problem persists, please contact us at help@unitybuildestimation.com'
        );
      }
    } catch (error) {
      console.error('Account creation error:', error);
      setSubmissionError(
        'An error occurred while creating the account. If the problem persists, please contact us at help@unitybuildestimation.com'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Custom text field styling for better contrast
  const textFieldSx = {
    '& .MuiInputBase-root': {
      backgroundColor: darkMode
        ? alpha(theme.palette.background.paper, 0.95)
        : alpha(theme.palette.background.paper, 0.98),
      borderRadius: 1,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: darkMode ? alpha(theme.palette.divider, 0.3) : alpha(theme.palette.divider, 0.5),
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    // Override browser autofill styling
    '& input:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 1000px ${
        darkMode
          ? alpha(theme.palette.background.paper, 0.95)
          : alpha(theme.palette.background.paper, 0.98)
      } inset !important`,
      WebkitTextFillColor: `${theme.palette.text.primary} !important`,
      borderRadius: '4px !important',
    },
    '& input:-webkit-autofill:hover': {
      WebkitBoxShadow: `0 0 0 1000px ${
        darkMode
          ? alpha(theme.palette.background.paper, 0.95)
          : alpha(theme.palette.background.paper, 0.98)
      } inset !important`,
      WebkitTextFillColor: `${theme.palette.text.primary} !important`,
    },
    '& input:-webkit-autofill:focus': {
      WebkitBoxShadow: `0 0 0 1000px ${
        darkMode
          ? alpha(theme.palette.background.paper, 0.95)
          : alpha(theme.palette.background.paper, 0.98)
      } inset !important`,
      WebkitTextFillColor: `${theme.palette.text.primary} !important`,
    },
    '& input:-webkit-autofill:active': {
      WebkitBoxShadow: `0 0 0 1000px ${
        darkMode
          ? alpha(theme.palette.background.paper, 0.95)
          : alpha(theme.palette.background.paper, 0.98)
      } inset !important`,
      WebkitTextFillColor: `${theme.palette.text.primary} !important`,
    },
  };

  return (
    <Container maxWidth="xs">
      <Paper
        elevation={3}
        sx={{
          mt: 4,
          p: 4,
          backgroundColor: darkMode
            ? alpha(theme.palette.background.paper, 0.95)
            : alpha(theme.palette.background.paper, 0.98),
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: 1,
          borderColor: alpha(theme.palette.divider, 0.2),
        }}
      >
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Create Account
          </Typography>
          <Typography variant="body1" color="text.secondary">
            If you have an active account, please use the login button above.
          </Typography>
        </Box>
        <form onSubmit={handleInitiateVerification}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Account Name"
                value={accountName}
                onChange={handleAccountNameChange}
                error={!!errors.accountName}
                helperText={errors.accountName}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="User Name"
                value={userName}
                onChange={handleUserNameChange}
                error={!!errors.username}
                helperText={errors.username}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Account / User Email"
                type="email"
                value={accountEmail}
                onChange={handleAccountEmailChange}
                error={!!errors.email}
                helperText={errors.email}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Account Phone Number"
                type="tel"
                value={accountPhone}
                onChange={handleAccountPhoneNumberChange}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="User Password"
                type={showPassword1 ? 'text' : 'password'}
                value={userPassword}
                onChange={e => setUserPassword(e.target.value)}
                error={!!errors.password}
                helperText={errors.password}
                sx={textFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword1(!showPassword1)} edge="end">
                        {showPassword1 ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Confirm User Password"
                type={showPassword2 ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                sx={textFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword2(!showPassword2)} edge="end">
                        {showPassword2 ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label={`What is ${captchaQuestion.a} + ${captchaQuestion.b}?`}
                value={captchaAnswer}
                onChange={e => setCaptchaAnswer(e.target.value)}
                error={!!errors.captcha}
                helperText={errors.captcha}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox checked={termsOfService} onChange={handleTermsOfServiceChange} />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link component="button" onClick={() => setTermsDialogOpen(true)}>
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link component="button" onClick={() => setPrivacyDialogOpen(true)}>
                      Privacy Policy
                    </Link>
                  </Typography>
                }
              />
              {errors.terms && <Alert severity="error">{errors.terms}</Alert>}
            </Grid>
            {submissionError && (
              <Grid item xs={12}>
                <Alert severity="error">{submissionError}</Alert>
              </Grid>
            )}
            {verificationError && (
              <Grid item xs={12}>
                <Alert severity="error">{verificationError}</Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                disabled={
                  sendingVerification ||
                  isSubmitting ||
                  !accountEmail ||
                  !termsOfService ||
                  !accountName ||
                  !accountPhone ||
                  !userPassword ||
                  !confirmPassword ||
                  !captchaAnswer
                }
              >
                {sendingVerification
                  ? 'Sending Verification...'
                  : isSubmitting
                    ? 'Creating Account...'
                    : 'Verify Email & Continue'}
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                If you need assistance, please contact us at{' '}
                <Link
                  href="mailto:help@unitybuildestimation.com"
                  sx={{
                    color: theme.palette.primary.main,
                    position: 'relative',
                    top: '1px',
                  }}
                >
                  help@unitybuildestimation.com
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </form>

        {/* Verification Code Dialog */}
        <Dialog
          open={verificationDialogOpen}
          aria-labelledby="verification-dialog-title"
          aria-describedby="verification-dialog-description"
        >
          <DialogTitle id="verification-dialog-title">Email Verification</DialogTitle>
          <DialogContent>
            <DialogContentText id="verification-dialog-description">
              We've sent a verification code to <strong>{accountEmail}</strong>. Please check your
              email and enter the code below to continue.
            </DialogContentText>

            <TextField
              autoFocus
              margin="dense"
              id="verification-code"
              label="Verification Code"
              type="text"
              fullWidth
              variant="outlined"
              value={userEnteredCode}
              onChange={e => setUserEnteredCode(e.target.value)}
              error={!!verificationError}
              helperText={verificationError}
              sx={textFieldSx}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVerificationDialogOpen(false)} color="primary">
              Back to Form
            </Button>
            <Button
              onClick={verifyCode}
              color="primary"
              variant="contained"
              disabled={!userEnteredCode}
            >
              Verify
            </Button>
          </DialogActions>
        </Dialog>

        <TermsOfServiceDialog open={termsDialogOpen} onClose={() => setTermsDialogOpen(false)} />
        <PrivacyPolicyDialog open={privacyDialogOpen} onClose={() => setPrivacyDialogOpen(false)} />
      </Paper>
    </Container>
  );
};
